import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { setTenantCurrency } from '@/lib/currency';

/**
 * useTaxConfig - single source of truth for VAT/Tax settings across the app.
 * Returns effective config for the given branch (falls back to tenant defaults).
 *
 * Usage:
 *   const { taxConfig, loading, refresh } = useTaxConfig(branchId);
 *   // taxConfig = { vat_enabled, vat_rate, vat_inclusive, country_code, ... }
 */

// Simple in-memory cache keyed by branch_id to avoid re-fetching.
// iter930 — added in-flight promise dedup so many components mounting in the
// same render cycle share ONE network call instead of each firing their own.
const cache = new Map();
const inflight = new Map();
const LISTENERS = new Set();

const FALLBACK = { vat_enabled: false, vat_rate: 15, vat_inclusive: false, country_code: 'sa', currency: 'SAR' };

export function invalidateTaxConfig() {
  cache.clear();
  inflight.clear();
  LISTENERS.forEach(fn => fn());
}

// Shared cached loader — used by both useTaxConfig() and BranchTaxBadge so the
// effective tax-config endpoint is hit at most once per branch per session.
export async function loadTaxConfig(branchId) {
  const key = branchId || '__default__';
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const res = await api.get('/accounting/tax-config/effective', { params });
      cache.set(key, res.data);
      if (res.data?.currency) setTenantCurrency(res.data.currency);
      return res.data;
    } catch {
      cache.set(key, FALLBACK);
      return FALLBACK;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export function useTaxConfig(branchId) {
  const key = branchId || '__default__';
  const [taxConfig, setTaxConfig] = useState(cache.get(key) || null);
  const [loading, setLoading] = useState(!cache.has(key));

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    const data = await loadTaxConfig(branchId);
    setTaxConfig(data);
    setLoading(false);
    return data;
  }, [branchId]);

  useEffect(() => {
    let alive = true;
    if (cache.has(key)) {
      setTaxConfig(cache.get(key));
      setLoading(false);
    } else {
      loadTaxConfig(branchId).then(d => { if (alive) { setTaxConfig(d); setLoading(false); } });
    }
    const listener = () => { fetchConfig(); };
    LISTENERS.add(listener);
    return () => { alive = false; LISTENERS.delete(listener); };
  }, [key, branchId, fetchConfig]);

  return { taxConfig, loading, refresh: fetchConfig };
}

/**
 * Pure helper to compute VAT given amount + config + inclusive flag.
 * Returns { net, vat, gross }
 */
export function computeVat({ amount = 0, rate = 0, inclusive = false, enabled = true } = {}) {
  const amt = parseFloat(amount) || 0;
  if (!enabled || !rate) return { net: amt, vat: 0, gross: amt };
  const r = parseFloat(rate) / 100;
  if (inclusive) {
    const net = amt / (1 + r);
    const vat = amt - net;
    return { net: round(net), vat: round(vat), gross: round(amt) };
  }
  const vat = amt * r;
  return { net: round(amt), vat: round(vat), gross: round(amt + vat) };
}

function round(v) { return Math.round(v * 100) / 100; }
